import { MaximizeIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Image } from '@components/image';
import { Group } from '@components/layout';
import { OptionsMenu } from '@components/menu';
import { useEditWorkspaceThumbnailMutation } from '@generated/graphql';
import { useTheme } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { useState } from 'react';
import { DatasetsDetailsImagesSliderInferenceOverlay } from './DatasetsDetailsImagesSliderInferenceOverlay';
import { DatasetsDetailsImagesSliderOverlay } from './DatasetsDetailsImagesSliderOverlay';
import { useImageOverlayContext } from './DatasetsDetailsImagesSliderOverlayContext';
import { FullScreenImageModal } from './FullScreenImageModal';

const OverlaySVG = (
  imageDimensions: [number, number],
  imageRatio: [number, number],
  imageIndex: number,
) => (
  <>
    <DatasetsDetailsImagesSliderOverlay
      imageIndex={imageIndex}
      renderErrors={null}
      setRenderErrors={null}
    />
    <DatasetsDetailsImagesSliderInferenceOverlay
      index={0}
      imageDimensions={imageDimensions}
      imageRatio={imageRatio}
    />
    <DatasetsDetailsImagesSliderInferenceOverlay
      index={1}
      imageDimensions={imageDimensions}
      imageRatio={imageRatio}
    />
  </>
);

type ImageViewerProps = {
  imageIndex: number;
  url: string;
  workspaceId: string;
  onPrevClick: () => void;
  onNextClick: () => void;
};

export const ImageViewer = ({
  imageIndex,
  url,
  workspaceId,
  onPrevClick,
  onNextClick,
}: ImageViewerProps) => {
  const { palette } = useTheme();
  const { groundTruthSettings, groundTruthData } = useImageOverlayContext();

  const { createAsyncNotification } = useNotifications();
  const [editWorkspaceThumbnail] = useEditWorkspaceThumbnailMutation();

  const handleSetThumbnail = createAsyncNotification(
    'You have successfully changed the workspace thumbnail',
    async (thumbnailUrl: string) => {
      await editWorkspaceThumbnail({
        variables: {
          workspaceId,
          thumbnail: thumbnailUrl,
        },
      });
    },
  );

  const [fullScreenImageModalOpen, setFullScreenImageModalOpen] = useState(false);

  return (
    <Group height="100%" width="100%" sx={{ position: 'relative', flex: 1 }}>
      <Image
        preload
        url={url}
        onPrevClick={onPrevClick}
        onNextClick={onNextClick}
        overlaySVG={(imageDimensions, imageRatio) =>
          OverlaySVG(imageDimensions, imageRatio, imageIndex)
        }
        overlayStyle={
          groundTruthData &&
          groundTruthSettings.mode === 'segmentation' &&
          groundTruthSettings.segmentationMask
            ? { backgroundColor: 'black' }
            : {}
        }
      />
      <IconButton
        Icon={MaximizeIcon}
        sx={{
          position: 'absolute',
          top: 8,
          right: 32,
          borderRadius: '20px',
          backgroundColor: palette.background.default,
        }}
        onClick={() => setFullScreenImageModalOpen(true)}
      />
      <FullScreenImageModal
        url={url}
        overlaySVG={(imageDimensions, imageRatio) =>
          OverlaySVG(imageDimensions, imageRatio, imageIndex)
        }
        overlayStyle={
          groundTruthData &&
          groundTruthSettings.mode === 'segmentation' &&
          groundTruthSettings.segmentationMask
            ? { backgroundColor: 'black' }
            : {}
        }
        open={fullScreenImageModalOpen}
        onClose={() => setFullScreenImageModalOpen(false)}
      />
      <OptionsMenu
        sx={{
          position: 'absolute',
          right: 0,
          top: 8,
          borderRadius: '20px',
          backgroundColor: palette.background.default,
        }}
        items={[
          {
            label: 'Set workspace thumbnail',
            onClick: () => handleSetThumbnail(url),
          },
        ]}
        menuProps={{ transformOrigin: { vertical: 'top', horizontal: 'right' } }}
      />
    </Group>
  );
};
